import { Injectable, inject } from '@angular/core';
import { ReservationSummaryStoreService } from './reservation-summary-store.service';
import { DataBagService } from '@/common/data-bag.service';
import { LoaderService } from '@/common/loader/loader.service';
import {
	Attachment,
	AvailabilityCheck,
	AvailabilityTransport,
	ChangeOfferSelected,
	ChangeOfferStop,
	Conversation,
	FilteredOrganization,
	Location,
	OfferChangeDialog,
	PatchOfferPayload,
	PatchReservationPayload,
	PatchUserDataFullPayload,
	ProductToServiceFilter,
	ProfileDocumentKind,
	Promotion,
	Reservation,
	ReservationOfferSend,
	UserContact,
	UserData,
	UserDataFullPayload,
	DateRange,
	Channel,
	OfferSlotToPatch,
	SeatNumberPayload,
	Result,
	OwnerUser,
	Service,
} from '../../reservation.model';
import { AvailabilityKind } from '@/common/enums/AvailabilityKind';
import { OfferChangeTypes } from '@/common/enums/OfferChangeType';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { PriceSettingsResult, SellAccommodation, SellTransport } from '../reservation-summary.model';

@Injectable({
	providedIn: 'root',
})
export class ReservationSummaryApiService {
	private readonly apiUrl = environment.apiUrl;

	private readonly httpClient = inject(HttpClient);
	private readonly _store = inject(ReservationSummaryStoreService);
	private readonly api = inject(DataBagService);
	private readonly loaderService = inject(LoaderService);

	getAccommodation(termId: number): Observable<SellAccommodation[]> {
		return this.httpClient.get<SellAccommodation[]>(`${this.apiUrl}/offer/sell_accommodation/${termId}/`);
	}

	getOfferPriceSettings(termId: number): Observable<PriceSettingsResult> {
		return this.httpClient.get<PriceSettingsResult>(`${this.apiUrl}/offer/sell_price_setting/${termId}/`);
	}

	getSellTransport(termId: number): Observable<SellTransport[]> {
		return this.httpClient.get<SellTransport[]>(`${this.apiUrl}/offer/sell_transport/${termId}/`);
	}

	getOrganization(params: string): Observable<FilteredOrganization[]> {
		return this.httpClient
			.get<Result<FilteredOrganization>>(`${this.apiUrl}/organization/${params}`)
			.pipe(map((result) => result.results));
	}

	getDataFull(params: string): Observable<OwnerUser[]> {
		return this.httpClient
			.get<Result<OwnerUser>>(`${this.apiUrl}/profile/data_full/${params}`)
			.pipe(map((result) => result.results));
	}

	getDocumentKinds(): Observable<ProfileDocumentKind[]> {
		return this.httpClient
			.get<Result<ProfileDocumentKind> | ProfileDocumentKind[]>(`${this.apiUrl}/profile/document_kind/`)
			.pipe(map((result) => (Array.isArray(result) ? result : (result.results ?? []))));
	}

	getLocations(): Observable<Location[]> {
		return this.httpClient
			.get<Result<Location>>(`${this.apiUrl}/location/?limit=9999`)
			.pipe(map((result) => result.results ?? []));
	}

	patchReservation(reservationId: number, payload: PatchReservationPayload): Observable<Reservation> {
		return this.httpClient.patch<Reservation>(`${this.apiUrl}/reservation/${reservationId}/`, payload);
	}

	createUserDataFull(userData: UserDataFullPayload): Observable<UserData> {
		return this.httpClient.post<UserData>(`${this.apiUrl}/profile/data_full/`, userData);
	}

	patchOfferSlot(slotId: number, payload: Partial<OfferSlotToPatch>): Observable<string> {
		return this.httpClient.patch<string>(`${this.apiUrl}/reservation/offer/slot/${slotId}/`, payload);
	}

	patchUserDataFull(userData: PatchUserDataFullPayload): Observable<UserData> {
		return this.httpClient.patch<UserData>(`${this.apiUrl}/profile/data_full/${userData.id}/`, userData);
	}

	assignUserDataToSlot(userData: PatchUserDataFullPayload): Observable<UserData> {
		return this.httpClient.patch<UserData>(`${this.apiUrl}/profile/data_full/${userData.id}/`, userData);
	}

	updateReservationOffer(
		reservationId: number,
		offerId: number,
		payload: ReservationOfferSend,
		force = false,
	): Observable<void> {
		return this.httpClient.put<void>(
			`${this.apiUrl}/reservation/${reservationId}/reservation_offer/${offerId}/?force=${force}`,
			payload,
		);
	}

	patchReservationService(payload: Partial<Service>): Observable<Service> {
		return this.httpClient.patch<Service>(`${this.apiUrl}/reservation/service/${payload.id}/`, payload);
	}

	actions(store = this._store, api = this.api, that = this) {
		const {
			setReservation,
			setCancellationReasons,
			setPayment,
			setDocument,
			setPaymentTerm,
			setOffers,
			setOfferService,
			setLoggedUser,
			setUsers,
			setConversations,
			setMessages,
		} = this._store.mutations();

		return {
			//GET
			getReservation(id: number): Promise<Reservation> {
				return new Promise((resolve, reject) => {
					that.loaderService.show();
					api.get(`/reservation/${id}/`, (result, error) => {
						if (result) {
							that.loaderService.hide();
							setReservation(result);
							resolve(result);
						} else {
							reject();
							throw new Error(that.processErrorMessage(error));
						}
					});
				});
			},
			getPayment(id: number): void {
				api.get(`/payment/?table_id__in=${id}&limit=9999`, (result, error) => {
					if (result) setPayment(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},
			getPaymentTerm(id: number): void {
				api.get(`/payment/term/?table_id__in=${id}`, (result, error) => {
					if (result) setPaymentTerm(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},
			getDocuments(resId: number): void {
				api.get(`/reservation/${resId}/documents/?limit=999`, (result, error) => {
					if (result) setDocument(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},
			getCancellationReasons(): void {
				api.get(`/reservation/cancellation_reason/`, (result, error) => {
					if (result) setCancellationReasons(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},

			getOfferFull(params: string, limit: number): Promise<void> {
				return new Promise((resolve) => {
					api.get(`/www/offer_all/${params}&limit=${limit}`, (result, error) => {
						if (result) {
							setOffers(result.results);
							resolve();
						} else throw new Error(that.processErrorMessage(error));
					});
				});
			},
			getBoards(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/board/`, (result, error) => {
						if (result) resolve((store.boards = result.results));
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getRoomTypes(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/product/room/type/?limit=100`, (result, error) => {
						if (result) resolve((store.roomTypes = result.results));
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getProducts(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(
						`/product/?limit=999`,

						(result, error) => {
							if (result) resolve((store.products = result.results));
							else reject(new Error(that.processErrorMessage(error)));
						},
					);
				});
			},
			getConveniences(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/product/convenience/?limit=500`, (result, error) => {
						if (result) resolve((store.conveniences = result.results));
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getLocations(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/location/?limit=9999`, (result, error) => {
						if (result) resolve((store.locations = result.results));
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getStops(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/transport/stop/?limit=999`, (result, error) => {
						if (result)
							resolve(
								(store.stops = result.results.filter((stop) => stop.location.locationKind === 'CT')),
							);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getProductKinds(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/product/kind/`, (result, error) => {
						if (result) resolve((store.productKinds = result.results));
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getPromotionsCatalog(): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.get(`/offer/catalog/`, (result, error) => {
						if (result) resolve((store.promotions = result.results));
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			getOfferService(params: string): void {
				api.get(`/offer/service/${params}`, (result, error) => {
					if (result) setOfferService(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},

			getLoggedUser(): void {
				api.get('/profile/', (result, error) => {
					if (result) setLoggedUser(result);
					else throw new Error(that.processErrorMessage(error));
				});
			},

			getUsers(): void {
				api.get('/profile/all_users/?limit=999', (result, error) => {
					if (result) setUsers(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},

			getConversations(resId: number): Promise<boolean> {
				return new Promise((resolve) => {
					api.get(`/conversation/threads/?table_id=${resId}&limit=999`, (result, error) => {
						if (result) {
							setConversations(result.results);
							resolve(true);
						} else throw new Error(that.processErrorMessage(error));
					});
				});
			},

			getConversation(id: string): Promise<Conversation> {
				return new Promise((resolve) => {
					api.get(`/conversation/threads/${id}/`, (result: Conversation, error) => {
						if (result) {
							resolve(result);
						} else {
							throw new Error(that.processErrorMessage(error));
						}
					});
				});
			},

			getMessages(conversationId: string): void {
				api.get(`/conversation/threads/${conversationId}/messages/`, (result, error) => {
					if (result) setMessages(result.results);
					else throw new Error(that.processErrorMessage(error));
				});
			},

			getAttachments(conversationId: string, messageId: string): Promise<Attachment[]> {
				return new Promise((resolve, reject) => {
					api.get(
						`/conversation/threads/${conversationId}/messages/${messageId}/attachments/`,
						(result: { results: Attachment[] }, error: any) => {
							if (result) resolve(result.results);
							else reject(error);
						},
					);
				});
			},

			getStopsByProductAndTerm(params: {
				dateRange: string;
				productId: number;
				transportDeparture: number | null;
				transportReturn: number | null;
			}): Promise<ChangeOfferStop[]> {
				let parameters = `?limit=999&date_range=${params.dateRange}&product=${params.productId}`;
				if (params.transportDeparture && params.transportReturn) {
					parameters += `&departure_id=${params.transportDeparture}&return_id=${params.transportReturn}`;
				}
				return new Promise((resolve, reject) => {
					api.get(
						`/transport/stop/name/${parameters}`,
						(result: { results: ChangeOfferStop[] }, error: any) => {
							if (result) resolve(result.results);
							else reject(error);
						},
					);
				});
			},

			getChannels(): Promise<Channel[]> {
				return new Promise((resolve, reject) => {
					api.get('/reservation/channel/', (result: { results: Channel[] }, error: any) => {
						if (result) resolve(result.results);
						else reject(error);
					});
				});
			},

			getPromotions(params: string): Promise<Promotion[]> {
				return new Promise((resolve, reject) => {
					api.get(`/www/promotions/${params}`, (result: { results: Promotion[] }, error: any) => {
						if (result) resolve(result.results);
						else reject(error);
					});
				});
			},

			getPromotion(id: number): Promise<Promotion> {
				return new Promise((resolve, reject) => {
					api.get(`/www/promotions/${id}/`, (result: Promotion, error: any) => {
						if (result) resolve(result);
						else reject(error);
					});
				});
			},

			checkRoomAvailability(
				offer: ChangeOfferSelected,
				currentOffer: OfferChangeDialog,
			): Promise<AvailabilityCheck> {
				/** Cases when we don't need to check room availability:
				 1. When we are changing the configuration of the same offer,
				 2. When we are changing offer to a new offer, and we don't have room type id (new offer could be an express one),
				 3. When term, product and room type ids are the same.
				**/
				return new Promise((resolve, reject) => {
					if (
						currentOffer.type === OfferChangeTypes.CONFIG ||
						!offer.roomType.id ||
						(currentOffer.type === OfferChangeTypes.CHANGE &&
							currentOffer.roomType?.id === offer.roomType.id &&
							currentOffer.product.id === offer.product.id &&
							currentOffer.term.id === offer.term)
					) {
						return resolve({
							type: AvailabilityKind.ROOM,
							available: true,
						});
					}
					const { lower, upper } = offer.dateRange;
					api.get(
						`/product/${offer.product.id}/is_room_type_available/?room_type_id=${offer.roomType.id}&lower=${lower}&upper=${upper}`,
						(result: { available: boolean }, error: any) => {
							if (result) {
								resolve({
									type: AvailabilityKind.ROOM,
									available: result.available,
								});
							} else reject(error);
						},
					);
				});
			},

			checkTransportAvailability(
				transport: AvailabilityTransport,
				transportChange: boolean,
			): Promise<AvailabilityCheck> {
				return new Promise((resolve, reject) => {
					if (!transport.departureId || !transport.returnId || !transportChange) {
						return resolve({
							type: AvailabilityKind.TRANSPORT,
							available: true,
						});
					}
					api.get(
						`/transport/available/${transport.departureId},${transport.returnId}/?stop=${transport.stopId}&people=${transport.personCount}`,
						(result: { available: boolean }, error: any) => {
							if (result) {
								resolve({
									type: AvailabilityKind.TRANSPORT,
									available: result.available,
								});
							} else reject(error);
						},
					);
				});
			},

			//PUT
			// TODO: Implement an interface for payload
			cancelReservation(id: number, payload: any): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.put(`/reservation/reservation/cancel/${id}/`, payload, (result, error) => {
						if (result) resolve(true);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			// TODO: Implement an interface for payload
			cancelReservationOffer(id: number, payload: any): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.put(`/reservation/reservation_offer/cancel/${id}/`, payload, (result, error) => {
						if (result) resolve(true);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			// TODO: Implement an interface for payload
			cancelReservationSlot(id: number, payload: any): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.put(`/reservation/reservation_offer_slot/cancel/${id}/`, payload, (result, error) => {
						if (result) resolve(true);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},

			updateSlot(slotId: number, payload: any): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.put(`/reservation/offer/slot/${slotId}/`, payload, (result, error) => {
						if (result) resolve(true);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},

			//PATCH
			updateResponsibleUser(userId: number, resId: number): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.patch(`/reservation/${resId}/`, { responsible_user: userId }, (result, error) => {
						if (result) resolve(true);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			patchOfferSlot(slotId: number, payload: Partial<OfferSlotToPatch>): Promise<string> {
				return new Promise((resolve, reject) => {
					api.patch(`/reservation/offer/slot/${slotId}/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			patchUserData(payload: UserData): Promise<string> {
				return new Promise((resolve, reject) => {
					api.patch(`/profile/data/${payload.id}/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},
			patchUserContact(payload: UserContact): Promise<string> {
				return new Promise((resolve, reject) => {
					api.patch(`/profile/contact/${payload.id}/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(new Error(that.processErrorMessage(error)));
					});
				});
			},

			patchThread(threadId: string, data: Partial<Conversation>) {
				return new Promise((resolve, reject) => {
					api.patch(`/conversation/threads/${threadId}/`, data, (result, error) => {
						if (error) reject(JSON.stringify(error.error));
						else resolve(true);
					});
				});
			},

			createUserData(payload: UserData): Promise<UserData | boolean> {
				return new Promise((resolve, reject) => {
					api.post('/profile/data/', payload, (result, error) => {
						if (result) resolve(result);
						else reject(false);
					});
				});
			},
			assignUserDataToSlot(slotId: number, userDataId: number): Promise<{ success: boolean; message?: string }> {
				return new Promise((resolve, reject) => {
					api.patch(`/reservation/offer/slot/${slotId}/`, { userData: userDataId }, (result, error) => {
						if (result)
							resolve({
								success: true,
							});
						else
							reject({
								success: false,
								message: that.processErrorMessage(error),
							});
					});
				});
			},
			patchOffer(payload: Partial<PatchOfferPayload>, id: number): Promise<unknown> {
				return new Promise((resolve, reject) => {
					api.patch(`/reservation/offer/${id}/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(that.processErrorMessage(error));
					});
				});
			},
			patchSeatNumber(payload: SeatNumberPayload[]): Promise<void> {
				return new Promise((resolve, reject) => {
					api.patch(`/transport/seat_number/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(that.processErrorMessage(error));
					});
				});
			},
			postSeatNumber(payload: SeatNumberPayload): Promise<void> {
				return new Promise((resolve, reject) => {
					api.post(`/transport/seat_number/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(that.processErrorMessage(error));
					});
				});
			},
			createUserDataFull(userData: UserDataFullPayload): Promise<UserData> {
				return new Promise((resolve, reject) => {
					api.post('/profile/data_full/', userData, (result, error) => {
						if (result) resolve(result);
						else reject(that.processErrorMessage(error));
					});
				});
			},
			patchUserDataFull(userData: PatchUserDataFullPayload): Promise<UserData> {
				return new Promise((resolve, reject) => {
					api.patch(`/profile/data_full/${userData.id}/`, userData, (result, error) => {
						if (result) resolve(result);
						else reject(that.processErrorMessage(error));
					});
				});
			},
			createOffer(id: number, data: ReservationOfferSend): Promise<ReservationOfferSend> {
				return new Promise((resolve, reject) => {
					api.post(`/reservation/${id}/reservation_offer/`, data, (result, error) => {
						if (result) resolve(result);
						else reject(that.processErrorMessage(error));
					});
				});
			},
			findUserData(fullname: string): Promise<UserData[]> {
				return new Promise((resolve, reject) => {
					api.get(`/profile/data_full/?full_name=${fullname}`, (result, error) => {
						if (result) resolve(result.results);
						else reject([]);
					});
				});
			},
			findOrganization(name: string): Promise<FilteredOrganization[]> {
				return new Promise((resolve, reject) => {
					api.get(`/organization/?limit=100&name=${name}`, (result, error) => {
						if (result) resolve(result.results);
						else reject([]);
					});
				});
			},
			getUserData(id: number): Promise<UserData> {
				return new Promise((resolve, reject) => {
					api.get(`/profile/data_full/${id}/`, (result, error) => {
						if (result) resolve(result);
						else reject(error);
					});
				});
			},
			patchReservation(payload: PatchReservationPayload): Promise<Reservation> {
				return new Promise((resolve, reject) => {
					api.patch(`/reservation/${payload.id}/`, payload, (result, error) => {
						if (result) resolve(result);
						else reject(error);
					});
				});
			},
			watchThread(threadId: string): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.post(`/conversation/threads/${threadId}/watch/`, null, (result, error) => {
						if (error) reject(JSON.stringify(error.error));
						else resolve(true);
					});
				});
			},

			unwatchThread(threadId: string): Promise<boolean> {
				return new Promise((resolve, reject) => {
					api.delete(`/conversation/threads/${threadId}/watch/delete/`, null, (result, error) => {
						if (error) reject(JSON.stringify(error.error));
						else resolve(true);
					});
				});
			},
		};
	}

	private processErrorMessage(error) {
		return JSON.stringify(error.error);
	}
}
